import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Link,
  SxProps,
  TextField,
  Theme,
  useTheme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import { usePostHog } from "@posthog/react";
import { Auth } from "aws-amplify";
import { Form, Formik } from "formik";
import { replace } from "lodash/fp";
import React, { createRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useMutation } from "react-query";
import * as Routes from "routes";
import { boolean as bool, object, ref, string } from "yup";
import "./style.css";

export const signUpValidationSchema = object({
  email: string().email("Enter a valid email").required("Email is required"),
  firstName: string().required("First Name is required."),
  lastName: string().required("Last Name is required."),
  password: string()
    .min(8, "Password should be of minimum 8 characters length")
    .required("Password is required."),
  confirmPassword: string()
    .oneOf([ref("password"), null], "Passwords must match")
    .required("Confirm Password is required."),
  termsOfUse: bool().oneOf(
    [true],
    "In order to continue, you must accept Geoscape Hub's Terms of Use."
  ),
});

type SignUpValues = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  termsOfUse: boolean;
};

function trimmessage(message: string): string {
  return replace(
    "..",
    ".",
    replace("PreSignUp failed with error ", "", message)
  );
}

export const SignUp = () => {
  const [recaptchaToken, setRecaptchaToken] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const posthog = usePostHog();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const recaptchaRef = createRef<ReCAPTCHA>();
  const recaptchaKey: string = import.meta.env.VITE_RECAPTCHA_KEY || "EMPTY";

  // React Query mutation for sign-up
  const signUpMutation = useMutation(
    async (values: SignUpValues & { recaptchaToken: string }) => {
      if (!values.recaptchaToken) {
        throw new Error("Please complete the reCaptcha challenge.");
      }

      await Auth.signUp({
        username: values.email.toLowerCase(),
        password: values.password,
        attributes: {
          given_name: values.firstName,
          family_name: values.lastName,
          "custom:dev_t_and_c": String(true),
        },
        validationData: {
          recaptcha_token: values.recaptchaToken,
        },
      });

      return { email: values.email };
    },
    {
      onSuccess: (data) => {
        posthog.capture("user_signed_up");
        posthog.identify(undefined, {
          email: data.email,
        });
      },
    }
  );

  const handleVerify = () => {
    const token = recaptchaRef?.current?.getValue();
    setRecaptchaToken(token || "");
    recaptchaRef?.current?.reset();
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleSubmit = (values: SignUpValues) => {
    signUpMutation.mutate({ ...values, recaptchaToken });
  };

  const getErrorMessage = (error: any): string => {
    if (!recaptchaToken) {
      return "Please complete the reCaptcha challenge.";
    }
    return trimmessage(error?.message || "An error occurred during sign up");
  };

  return (
    <Formik<SignUpValues>
      initialValues={{
        email: "",
        firstName: "",
        lastName: "",
        password: "",
        confirmPassword: "",
        termsOfUse: false,
      }}
      onSubmit={handleSubmit}
      validationSchema={signUpValidationSchema}
    >
      {({
        values: {
          email,
          firstName,
          lastName,
          password,
          confirmPassword,
          termsOfUse,
        },
        handleChange,
        handleBlur,
        errors,
        touched,
      }) => (
        <Form>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Card
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                minWidth: "340px",
                maxWidth: "500px",
                m: 2,
              }}
            >
              <CardHeader
                titleTypographyProps={{ variant: "h5" }}
                title="Create your Geoscape Hub account"
              />
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  width: "100%",
                  gap: "16px",
                }}
              >
                <TextField
                  id="email"
                  name="email"
                  label="Email"
                  type="text"
                  value={email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  disabled={signUpMutation.isLoading}
                  sx={customStyle}
                  InputProps={{
                    sx: {
                      borderRadius: "8px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignContent: "center",
                      alignItems: "center",
                    },
                  }}
                  fullWidth
                />
                <TextField
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  type="text"
                  value={firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.firstName && Boolean(errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                  disabled={signUpMutation.isLoading}
                  fullWidth
                  sx={customStyle}
                  InputProps={{
                    sx: {
                      borderRadius: "8px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignContent: "center",
                      alignItems: "center",
                    },
                  }}
                />
                <TextField
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  type="text"
                  value={lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.lastName && Boolean(errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                  disabled={signUpMutation.isLoading}
                  fullWidth
                  sx={customStyle}
                  InputProps={{
                    sx: {
                      borderRadius: "8px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignContent: "center",
                      alignItems: "center",
                    },
                  }}
                />
                <TextField
                  id="password"
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  disabled={signUpMutation.isLoading}
                  fullWidth
                  sx={customStyle}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                    sx: {
                      borderRadius: "8px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignContent: "center",
                      alignItems: "center",
                    },
                  }}
                />
                <TextField
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.confirmPassword && Boolean(errors.confirmPassword)
                  }
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  disabled={signUpMutation.isLoading}
                  fullWidth
                  sx={customStyle}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                    sx: {
                      borderRadius: "8px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignContent: "center",
                      alignItems: "center",
                    },
                  }}
                />
                <FormControlLabel
                  sx={{
                    display: "flex",
                    height: "38px",
                    margin: "0px",
                  }}
                  control={
                    <Checkbox
                      color="primary"
                      id="termsOfUse"
                      name="termsOfUse"
                      checked={termsOfUse}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <>
                      {"I agree to Geoscape Hub's "}
                      <a
                        href="https://geoscape.com.au/wp-content/uploads/2025/05/Geoscape-Australia-Geoscape-General-Terms-of-Use-v2.0-July-2025.pdf"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Terms of Use
                      </a>
                    </>
                  }
                />
                <FormHelperText error={true}>
                  {errors["termsOfUse"]}
                </FormHelperText>
                <Box width="100%" display="flex" justifyContent="center">
                  <ReCAPTCHA
                    id="recaptcha"
                    ref={recaptchaRef}
                    sitekey={recaptchaKey}
                    onChange={handleVerify}
                    theme={isDark ? "dark" : "light"}
                  />
                </Box>
                {signUpMutation.isLoading ? (
                  <Box width="100%" display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {signUpMutation.isSuccess && (
                      <Box
                        marginBottom={2}
                        marginTop={2}
                        style={{ width: "100%" }}
                      >
                        <Alert
                          variant="outlined"
                          severity="success"
                          sx={{
                            borderRadius: "8px",
                          }}
                        >
                          A verification email has been sent to:{" "}
                          {signUpMutation.data?.email}
                        </Alert>
                      </Box>
                    )}

                    {signUpMutation.isError && (
                      <Box
                        marginBottom={2}
                        marginTop={2}
                        style={{ width: "100%" }}
                      >
                        <Alert
                          variant="outlined"
                          severity="error"
                          sx={{
                            borderRadius: "8px",
                          }}
                        >
                          {getErrorMessage(signUpMutation.error)}
                        </Alert>
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      type="submit"
                      disabled={signUpMutation.isLoading}
                      fullWidth
                      sx={{ borderRadius: "8px", height: "36px" }}
                      children="Sign Up"
                    />

                    <Button
                      LinkComponent={Link}
                      disabled={signUpMutation.isLoading}
                      size="small"
                      fullWidth
                      href={Routes.signIn}
                    >
                      Sign In
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

const customStyle: SxProps<Theme> = {
  [`& .MuiInputLabel-root `]: {
    top: "-5px",
  },

  [`& .MuiInputLabel-shrink `]: {
    top: "0px",
  },
};
